import type { EncounterStepKey } from '@scripsteam/scrips-signal-ds';
import { STEP_TITLE_KEYS, sanitizeStepTitle, type StepTitleKey, type StepTitleOverrides } from './cps-layout';

/**
 * The allied-health PLAN step key (PROD-2040).
 *
 * WHY IT IS NOT AN `EncounterStepKey`. `EncounterStepKey` is owned by the Signal DS
 * (`@scripsteam/scrips-signal-ds`, dist/organisms/EncounterStep/EncounterStep.d.ts) and is a
 * closed 7-member union — `hpi | risk | ros | pe | docs | ap | wrap-up`. A consumer app cannot
 * add a member to it, and forking the union locally would be exactly the shadow-copy failure the
 * DS guards forbid. So the app WIDENS: `PaStepKey` is `EncounterStepKey` PLUS this one PA-local
 * key. Every existing `EncounterStepKey` value stays assignable, so nothing that types against
 * the DS union changes meaning — the widening is purely additive.
 *
 * The durable form is a DS release that adds the key (and an `EncounterStepAllied` wrapper beside
 * `EncounterStepDoctor`/`EncounterStepNurse`, widening `EncounterStepRole` off `'doctor' | 'nurse'`).
 * That is a change to the scrips-signal-ds repo, not this one; until it lands the key lives here.
 *
 * It is deliberately NOT `'ap'`. A/P is the physician's assessment-and-PLAN surface and it carries
 * ORDERS; the allied-health step terminates in a plan and prescribes nothing. Reusing `'ap'` would
 * hand an allied-health account the order-bearing canvas, and per the founder ruling A/P stays
 * hard-coded (physician-only, not configurable) — see STEP_BLOCKS in encounter-blocks.ts.
 *
 * The `as const` is belt-and-braces, NOT load-bearing — measured, not assumed: removing it and
 * running `npm run typecheck` still passes, because a `const` binding initialised with a string
 * literal already gets the literal type. What DOES collapse the union is an explicit `: string`
 * annotation here — then `PaStepKey` becomes `string`, every step-key check in the app silently
 * stops type-checking, and `tsc` stays green about it. That is the failure the compile-time
 * assertion at the top of encounter-blocks.allied.test.ts exists to catch (mutation-verified
 * against exactly that annotation), and it is why the type is left to be inferred here.
 */
export const ALLIED_PLAN_STEP = 'allied-plan' as const;

/**
 * [PROD-2949] The nurse's PLAN step key — the nursing care plan's own block (design desk D-66, 5 Oct:
 * a non-prescriber's plan IS their clinical output, so it gets its own home instead of a place inside
 * Wrap-up). It mirrors `ALLIED_PLAN_STEP` exactly: PA-local (not a DS `EncounterStepKey`, for the
 * same reason), never `'ap'` (A/P carries orders; a nurse never orders), and terminates in a plan.
 * The page hides it through `ctx.hiddenSteps` while `?ef=planSave` is off, so the nurse's flow is
 * unchanged until the switch is on.
 */
export const NURSING_PLAN_STEP = 'nursing-plan' as const;

/**
 * The PA encounter step vocabulary: the DS union WIDENED by the PA-local keys above.
 * `EncounterStepKey` remains a subtype, so a DS-typed value is still a valid `PaStepKey`.
 */
export type PaStepKey = EncounterStepKey | typeof ALLIED_PLAN_STEP | typeof NURSING_PLAN_STEP;

// Step ORDER + role visibility now live in the declarative block catalog (encounter-blocks.ts —
// STEP_BLOCKS / resolveSteps); the shell drives its nav from there. The old `doctorSteps` /
// `nurseSteps` / `stepsForRole` arrays were removed once the block engine landed (Wave 0/1).
// This module keeps only the short nav LABELS the shell renders.

/** Short nav label for each step key. Keyed exhaustively over `PaStepKey`, so a new step key
 *  cannot be added to the vocabulary without a nav label. */
export const STEP_LABELS: Record<PaStepKey, string> = {
  hpi: 'HPI',
  risk: 'Risk',
  ros: 'ROS',
  pe: 'PE',
  // Deployed labels, verbatim from the Flutter encounter tab list
  // (patient_chart/data/datamodels/ui_data.dart:1080, 1097): "Doc", not "Docs"; "Wrap-up",
  // not "Wrap-Up". The capital-U "Wrap-Up" at ui_data.dart:850 is the SECTION header inside
  // the step, which the step chrome still renders — a different seat, deliberately unchanged.
  docs: 'Doc',
  ap: 'A/P',
  'wrap-up': 'Wrap-up',
  // "Plan", not "A/P" and not "Orders": allied health terminates in a plan (PROD-2040).
  [ALLIED_PLAN_STEP]: 'Plan',
  // [PROD-2949] The same word for the nurse's plan block: the SECTION around it is "Nursing plan", so
  // the block does not repeat the section's title (one-title rule), exactly as allied "Plan" does.
  [NURSING_PLAN_STEP]: 'Plan',
};

/** Is this step one of the seven `EncounterStepKey` members whose title an author may override?
 *  `allied-plan` is PA-local, not part of the DS union the Studio knows about, and is never
 *  overridable. */
export function isStepTitleOverridable(key: PaStepKey): key is StepTitleKey {
  return (STEP_TITLE_KEYS as readonly string[]).includes(key);
}

/**
 * Return the short nav label for a step key.
 *
 * AUTHOR-CONTROLLED TITLES: a template may rename any of the seven `EncounterStepKey` steps
 * through the questionnaire's `cps-layout` root extension (`{ stepTitles: { hpi, risk, ros, pe,
 * docs, ap, wrap-up } }` — see cps-layout.ts). The fallback chain is deliberately total, because a
 * blank tab is worse than a generic one:
 *
 *   authored, non-blank, sanitized  →  the authored title
 *   absent key · absent map · empty · whitespace-only · non-string  →  STEP_LABELS[key]
 *   allied-plan (not overridable at all — PA-local, outside the DS union)  →  STEP_LABELS[key]
 *
 * `sanitizeStepTitle` runs HERE as well as at parse time — belt and braces, so a hand-built
 * override object (Storybook, a future caller) cannot smuggle a blank or unbounded title into the
 * nav either. The value is returned as plain text; every call site renders it as a React child.
 */
export function stepLabel(key: PaStepKey, titles?: StepTitleOverrides): string {
  // The overridable-step gate is enforced HERE too, not only by the type: `docs`/`ap`/`wrap-up`/
  // `allied-plan` are not questionnaire-sourced, so a runtime map that carries them must still be
  // ignored.
  const authored =
    titles && isStepTitleOverridable(key) ? sanitizeStepTitle(titles[key]) : undefined;
  return authored ?? STEP_LABELS[key];
}

/**
 * Frame v2 · the note BLOCK's name where it differs from the step's nav label (PROD-2947 integration,
 * the independent frame v2 review, 6 Oct): a block is named for its content, and the same word is used
 * for it everywhere in the note (its title, its section's summary line, the ribbon's step list).
 *  · `docs` — "Documents" (the nav keeps Flutter's "Doc"; the block holds the visit's documents).
 *  · `allied-plan` / `nursing-plan` — "Today's plan": the design desk's own name for the plan card
 *    (#617, D-66); in the note the card frame goes and the block carries the name, so the section
 *    ("Plan" / "Nursing plan") is never repeated by a "Plan" block inside it.
 * The switch-OFF frame never reads this: its step nav keeps `STEP_LABELS` exactly.
 */
export const NOTE_BLOCK_LABELS: Partial<Record<PaStepKey, string>> = {
  docs: 'Documents',
  [ALLIED_PLAN_STEP]: "Today's plan",
  [NURSING_PLAN_STEP]: "Today's plan",
};

/** A frame v2 note block's name: `NOTE_BLOCK_LABELS`, else the step's (possibly authored) label. */
export function noteBlockLabel(key: PaStepKey, titles?: StepTitleOverrides): string {
  return NOTE_BLOCK_LABELS[key] ?? stepLabel(key, titles);
}
